import { AgxToast } from '@urbanx/agx-ui-components';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { MenuTitles, Pages } from 'constants/menu';
import { AgxRow, AgxSaveChangesButton, SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2';
import { FormPrompt } from 'components/FormPrompt';
import { AgxToastState } from 'types/commonTypes';
import { SubPage } from 'components/left-submenu/LeftSubmenu';
import { ContractPageScaffold } from './ContractPageScaffold';

interface ContractSubPageProps {
  pageTitle: string;
  secondaryActiveMenu: MenuTitles;
  currentPage: Pages;
  subPages: SubPage[];
  onSaveChanges: (success: () => void) => void;
  children: React.ReactNode;
  isFormDirty: boolean;
  isSaving: boolean;
  isError: boolean;
  hideSaveButton?: boolean;
  secondaryActions?: ReactElement;
}

export const ContractSubPage = ({
  pageTitle,
  secondaryActiveMenu,
  currentPage,
  subPages,
  onSaveChanges,
  children,
  isFormDirty,
  isSaving,
  isError,
  hideSaveButton = false,
  secondaryActions,
}: ContractSubPageProps) => {
  const [hasSaved, setHasSaved] = useState(false);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const savingState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING;
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED;
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED;
    if (isError) return SAVE_BUTTON_STATES.ERROR;
    return SAVE_BUTTON_STATES.UNSAVED;
  }, [isSaving, isError, isFormDirty, hasSaved]);

  useEffect(() => {
    if (isFormDirty) {
      setHasSaved(false);
    }
  }, [isFormDirty]);

  useEffect(() => {
    if (savingState === SAVE_BUTTON_STATES.SAVED) {
      updateToastState({
        color: 'success',
        message: 'Changes saved successfully',
        open: true,
      });
    }
  }, [savingState]);

  return (
    <ContractPageScaffold
      pageTitle={pageTitle}
      secondaryActiveMenu={secondaryActiveMenu}
      currentPage={currentPage}
      subPages={subPages}
      secondaryActions={secondaryActions}
      beforeContent={
        <>
          <AgxToast selector="#agxToast" toastState={toastState} />
          <FormPrompt hasUnsavedChanges={isFormDirty} />
        </>
      }
      headerAction={
        !hideSaveButton ? (
          <AgxRow gap="spacing-md" fill alignItems="alignCenter" justifyContent="right">
            <AgxSaveChangesButton
              state={savingState}
              id="btnSaveChanges"
              text="Save Changes"
              size="small"
              variant="primary"
              onClick={() =>
                onSaveChanges(() => {
                  setHasSaved(true);
                })
              }
              disabled={!isFormDirty}
            />
          </AgxRow>
        ) : undefined
      }
    >
      {children}
    </ContractPageScaffold>
  );
};
